import { useEffect } from "react";
import { getIdToken, getIdTokenExpiry } from "../auth/tokenService";

/**
 * Keeps the SSO (MSAL / Entra ID) session alive while the user is signed in.
 *
 * Why this hook exists:
 * The axios interceptor refreshes the token reactively, but only when a request
 * is actually made. A user parked on a Power BI report can go 30+ minutes
 * without firing a single API call, so the id_token would silently expire and
 * their next interaction would bounce them through a full-page auth redirect.
 * This hook refreshes proactively on a timer so that never happens.
 *
 * Only relevant for the SSO flow. The CIAM flow has its own refresh mechanism
 * (see `refreshCallBack` in App.js), and `getIdToken` short-circuits for it.
 *
 * @param {boolean} enabled - Pass the "user is signed in" flag. When false the
 *   hook is completely inert, so it is safe to call unconditionally (React
 *   requires hooks to run in the same order on every render).
 */

// Refresh this far ahead of expiry so a slow network round-trip can't let the
// token lapse in the gap between scheduling and completion.
const BUFFER_MS = 5 * 60 * 1000;

// Floor on the timer. Without it, a token that is already expired (or expiring
// within the buffer) would compute a negative delay and fire in a tight loop.
const MIN_DELAY_MS = 60 * 1000;

// Circuit breaker. If the expiry stops advancing, refreshing again won't help
// and would just hammer Entra until it throttles us, so we give up instead.
const MAX_CONSECUTIVE_NO_PROGRESS = 3;

const useTokenRefresh = (enabled) => {
  useEffect(() => {
    // Not signed in (or signed out): register nothing and skip the cleanup.
    if (!enabled) return;

    let timer;
    // Guards against a refresh that resolves after the component unmounted,
    // which would otherwise schedule a new timer nobody can ever clear.
    let cancelled = false;
    // Baseline used to detect whether each refresh actually moved expiry forward.
    let lastExpiry = getIdTokenExpiry();
    let noProgressCount = 0;

    /**
     * Queues the next refresh based on how much life the current token has left.
     * Re-read the expiry each time rather than assuming a fixed lifetime, since
     * Entra can hand back tokens with different durations (CA policies, etc.).
     */
    const scheduleNext = () => {
      const exp = getIdTokenExpiry();
      // `exp` is seconds since epoch (a JWT claim); Date.now() is milliseconds.
      // Fall back to 30 min if there's no account/claims yet, so we still retry.
      const msUntilExpiry = exp ? exp * 1000 - Date.now() : 30 * 60 * 1000;
      const delay = Math.max(msUntilExpiry - BUFFER_MS, MIN_DELAY_MS);
      timer = setTimeout(tick, delay);
    };

    const tick = async () => {
      // forceRefresh bypasses the MSAL cache and redeems the refresh token,
      // guaranteeing a genuinely new id_token rather than the cached one.
      await getIdToken({ forceRefresh: true });
      if (cancelled) return;

      const exp = getIdTokenExpiry();

      // Sanity check: a successful refresh should push expiry further out. If it
      // didn't, the claims aren't being updated and rescheduling is pointless —
      // the MIN_DELAY_MS floor would turn this into a once-a-minute retry storm.
      if (exp && lastExpiry && exp <= lastExpiry) {
        noProgressCount += 1;
        if (noProgressCount >= MAX_CONSECUTIVE_NO_PROGRESS) {
          console.warn(
            "[useTokenRefresh] Token expiry is not advancing; stopping the refresh loop.",
          );
          // Bail without rescheduling. The axios 401 handler is still in place
          // as a fallback, so the user isn't stranded.
          return;
        }
      } else {
        // Made progress — reset the strike count.
        noProgressCount = 0;
      }
      lastExpiry = exp;

      scheduleNext();
    };

    // Kick off the cycle. Deliberately schedules rather than refreshing straight
    // away: SSOAuthGuard already fetches a token on mount, so an immediate call
    // here would be a redundant round-trip.
    scheduleNext();

    // Background tabs get their timers heavily throttled by the browser, so a
    // long-backgrounded tab can come back with an expired token and a timer that
    // never fired. Refresh on return to cover that case.
    const onVisible = () => {
      if (document.visibilityState === "visible") getIdToken();
    };
    document.addEventListener("visibilitychange", onVisible);

    // Cleanup on unmount or when `enabled` flips (e.g. on logout).
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);
};

export default useTokenRefresh;